import Markdown from "./Markdown"
import AirplayIcon from "@mui/icons-material/Airplay"
import CheckIcon from "@mui/icons-material/Check"
import ComputerIcon from "@mui/icons-material/Computer"
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo"
import { useMediaQuery, Button, Typography, Avatar, Box, styled } from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import { ActivityIndicator } from "components/activity-indicator"
import Attachment from "components/attachments/Attachment"
import useConventionSessionRealTimeCapacity from "hooks/sessions/useConventionSessionRealTimeCapacity"
import useScheduleSession from "hooks/sessions/useScheduleSession"
import useAuthRedirect from "hooks/useAuthRedirect"
import useRelativeTime from "hooks/useRelativeTime"
import { api } from "lib/api"
import openWindowAsync from "lib/open-window-async"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import SessionAttachment from "models/convention-api/v1/SessionAttachment"
import moment from "moment-timezone"
import { Dispatch, SetStateAction, useEffect } from "react"
import { pxToRem } from "utils/helpers"

const useStyles = makeStyles()(
  (theme) => ({
    sessionDiv: {
      fontSize: "14px",
      paddingTop: "30px",
      paddingBottom: "30px",
      borderBottom: "1px solid rgba(172, 177, 179, 1)",
    },
    sessionHeading: {
      fontFamily: "Raleway",
      fontWeight: 700,
      fontSize: "16px",
    },
    sessionActionBtn: {
      fontWeight: "bold",
      fontSize: "14px",
      color: theme.palette.primary.main,
    },
    liveBadge: {
      borderRadius: "10px",
      backgroundColor: "black",
      marginTop: "1rem",
      padding: "10px",
      color: "#ffff",
      fontSize: "14px",
    },
    avatar: {
      backgroundColor: theme.palette.primary.main,
      height: "40px",
      width: "40px",
      fontSize: "16px",
      fontWeight: "bold",
    },
  })
)

interface IStageSession {
  delayFactor: number
  session: ConventionSession
  onStageSessionId?: string
  displayOnStage: Dispatch<SetStateAction<any>>
  onStage: boolean
}

const StageSession = (props: IStageSession) => {
  const { delayFactor, session, onStageSessionId, displayOnStage, onStage } = props

  const description = session.description
  const endAt = session.end_at
  const startAt = moment(session.start_at)
  const adjustedStartAt = startAt.clone().subtract(5, "minutes") // 5 minutes before start time
  const name = session.name

  const { classes } = useStyles()
  const actualSessionRelativeTime = useRelativeTime({
    startAt,
    endAt,
  }) // default precision (1s)
  const adjustedSessionRelativeTime = useRelativeTime({
    startAt: adjustedStartAt,
    endAt,
  }) // default precision (1s)
  const isSmallScreen = useMediaQuery("(max-width: 640px)")

  const capacity = useConventionSessionRealTimeCapacity(session)

  const formattedStartTime = moment(startAt).format("hh:mm a z")
  const timeDuration = endAt.diff(startAt, "minutes")

  const sessionCapacity = useConventionSessionRealTimeCapacity(session)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        onStageSessionId === session.id &&
        sessionCapacity?.empty &&
        adjustedSessionRelativeTime.after
      ) {
        displayOnStage(null)
        return
      }

      if (onStageSessionId) return

      if (!sessionCapacity?.empty || adjustedSessionRelativeTime.during) displayOnStage(session)
    }, delayFactor * 100)
    return () => clearTimeout(timer)
  }, [
    adjustedSessionRelativeTime.during,
    adjustedSessionRelativeTime.after,
    onStageSessionId,
    sessionCapacity?.empty,
  ])

  const authRedirect = useAuthRedirect()

  const scheduleSession = useScheduleSession(session)

  const openAttachment = (attachment: SessionAttachment) => () => {
    authRedirect(() =>
      openWindowAsync(async () => {
        await api.createSessionAttachmentView({ req: undefined }, attachment.id)
        return attachment.url
      }),
    )
  }

  const closed = capacity?.empty && adjustedSessionRelativeTime.after
  const open = !capacity?.empty || adjustedSessionRelativeTime.during

  return (
    <>
      <Box
        display="flex"
        flexDirection={["row"]}
        width="100%"
        flexWrap="wrap"
        alignItems="flex-start"
        justifyContent="space-between"
        className={classes.sessionDiv}>
        <Box display="flex" flexDirection="column" width={["25%", "15%"]}>
          <Typography style={{ fontSize: "14px" }}>{formattedStartTime}</Typography>

          <Typography
            style={{
              fontSize: "14px",
              marginTop: "5px",
              color: "#767676",
            }}>
            {timeDuration} mins
          </Typography>

          {isSmallScreen && actualSessionRelativeTime.during && (
            <div className={classes.liveBadge} style={{ width: "48px" }}>
              Live
            </div>
          )}
        </Box>

        <Box display="flex" flexDirection={["column", "row"]} width={["75%", "85%"]}>
          <Box display="flex" flexDirection="column" width={["100%", "65%"]} mb={[15]}>
            <Box display="flex" alignItems="center" className={classes.sessionHeading}>
              <ComputerIcon style={{ marginRight: "10px" }} />
              {name}
            </Box>

            <_MarkdownContainer>
              <Markdown allow={["emphasis"]}>{description}</Markdown>
            </_MarkdownContainer>

            <Box display="flex" mt={[2]} mb={[2]} flexWrap="wrap" width={["100%"]}>
              {(session.assignments ?? []).map((assignment) => {
                return (
                  <Box
                    display="flex"
                    key={assignment.id}
                    mt={[2]}
                    mr={[4]}
                    mb={[2]}
                    alignItems="center">
                    <Avatar
                      className={classes.avatar}
                      aria-label={
                        // @ts-ignore
                        assignment.admin_user.first_name + assignment.admin_user.first_name
                      }
                      // @ts-ignore
                      src={assignment.admin_user.avatar.thumb_url}>
                      {/*
                      // @ts-ignore*/}
                      {assignment.admin_user.avatar.thumb_url
                        ? ""
                        : // @ts-ignore
                          assignment.admin_user.first_name.charAt(0).toUpperCase() +
                          // @ts-ignore
                          assignment.admin_user.last_name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box display="flex" ml={[2]} flexDirection="column">
                      <Typography>
                        {/*
                        // @ts-ignore*/}
                        {`${assignment.admin_user.first_name} ${assignment.admin_user.last_name}`}
                      </Typography>
                      <Typography
                        style={{
                          color: "rgba(118, 118, 118, 1)",
                        }}>
                        {/*
                        // @ts-ignore*/}
                        {assignment.admin_user.title}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>
            <Box
              display="flex"
              mt={[2]}
              mb={[2]}
              alignItems="flex-start"
              flexDirection="column"
              width={["100%"]}>
              {(session.attachments ?? []).map((attachment) => (
                <Attachment
                  caption={attachment.caption}
                  key={attachment.id}
                  onClick={openAttachment(attachment)}
                />
              ))}
            </Box>
          </Box>

          {!scheduleSession.isLoading ? (
            <Box display="flex" justifyContent={["flex-start", "flex-end"]} width={["100%", "35%"]}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems={["flex-start", "flex-end"]}
                width={["100%"]}>
                {closed && (
                  <Typography
                    style={{
                      fontStyle: "italic",
                    }}>
                    This session has ended.
                  </Typography>
                )}

                {open && (
                  <>
                    {onStage ? (
                      <Box display="flex" alignItems="center" fontSize="16px">
                        <OndemandVideoIcon style={{ fontSize: "16px", marginRight: "8px" }} />
                        Currently on Stage
                      </Box>
                    ) : (
                      <Button
                        variant="text"
                        className={classes.sessionActionBtn}
                        startIcon={<AirplayIcon />}
                        onClick={() => displayOnStage(session)}>
                        Display on Stage
                      </Button>
                    )}

                    {!isSmallScreen && actualSessionRelativeTime.during && (
                      <div className={classes.liveBadge} style={{ width: "80px" }}>
                        Live Now
                      </div>
                    )}
                  </>
                )}

                {adjustedSessionRelativeTime.before && !scheduleSession.isScheduled && (
                  <Button
                    variant="text"
                    className={classes.sessionActionBtn}
                    onClick={scheduleSession.schedule}>
                    Add to my schedule
                  </Button>
                )}

                {adjustedSessionRelativeTime.before && scheduleSession.isScheduled && (
                  <>
                    <Box display="flex" alignItems="center">
                      <CheckIcon style={{ fontSize: "16px", marginRight: "8px" }} />
                      Added to your schedule!
                    </Box>

                    <Button
                      className={classes.sessionActionBtn}
                      onClick={scheduleSession.undoSchedule}
                      variant="text">
                      Remove
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems={["flex-start", "flex-end"]}
              width={["30%"]}>
              <ActivityIndicator indicatorSize={15} />
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}
export default StageSession

const _MarkdownContainer = styled(Box)(() => ({
  width: "100%",
  marginTop: pxToRem(8),
  marginBottom: pxToRem(8),
}))
